import { useState, useEffect, useRef } from 'react';

import { useUserProfile } from '../../quiz-core/useStore';

import { mapPresenceStateToAppPlayers } from './presenceUtils';
import { useMultiplayerStore } from './useMultiplayerStore';
import { usePresencePolling } from './usePresencePolling';
import { usePresenceState } from './usePresenceState';
import { usePresenceUpdate } from './usePresenceUpdate';

export function useRoomPresence(roomId: string, userId: string | null, channel: any) {
  if (!userId) {
    // Safe default: multiplayer presence requires a userId
    return {
      players: [],
      presenceReady: false,
      channelStatus: 'INIT',
      error: 'No user ID',
      updatePresence: () => {},
    };
  }
  // --- State & Store ---
  const { players, setPlayers } = useMultiplayerStore();
  const [error, setError] = useState<string | null>(null);
  const [presenceReady, setPresenceReady] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [activeChannels] = useState<Map<string, any>>(() => new Map());

  // --- User Profile ---
  const userProfile = useUserProfile();
  
  // Debug logging to understand profile data in presence
  console.log('[useRoomPresence] userProfile data:', {
    id: userProfile.id,
    name: userProfile.name,
    loading: userProfile.loading
  });
  
  // Ensure we use actual profile name, with better fallback logic
  const displayName = (userProfile.name && userProfile.name.trim() !== '' && userProfile.name !== 'Player') 
    ? userProfile.name 
    : `User_${userId?.slice(-4)}` || 'Anonymous Player';
    
  const profileForPresence = {
    name: displayName,
    avatar_url: userProfile.avatar_url || null,
  };
  const joinKey = `${roomId}:${userId}`;

  // Ensure profile is loaded before proceeding with presence
  useEffect(() => {
    if (!userProfile.loading && userProfile.id && !userProfile.name) {
      console.log('[useRoomPresence] Profile loaded but name missing, fetching fresh profile...');
      userProfile.fetchProfile?.();
    }
  }, [userProfile.loading, userProfile.id, userProfile.name]);

  // --- Multiplayer Store Player Management ---
  // Add/update/remove player helpers
  const updatePresenceState = async (presenceList: any[]) => {
    if (typeof syncPlayersFromPresence === 'function') {
      syncPlayersFromPresence(presenceList);
    } else {
      setPlayers([]);
    }
  };

  const handleJoin = async (player: any) => {
    setPlayers((prev: any[]) => {
      // Add player if not present
      if (!prev.some((p) => p.id === player.id)) return [...prev, player];
      return prev;
    });
  };
  const removePlayer = (playerId: string) => {
    setPlayers((prev: any[]) => prev.filter((p) => p.id !== playerId));
  };

  // --- Presence Channel ---
  const presenceChannel = useRef(channel);
  const channelStatus = channel ? 'SUBSCRIBED' : 'INIT';

  // --- Presence State Management ---
  const {
    syncPlayersFromDb,
    syncPlayersFromPresence,
  } = usePresenceState(roomId);

  // --- Presence Sync Handler ---
  const handlePresenceSync = (presenceState: any) => {
    console.log('[useRoomPresence] Raw presence state received:', presenceState);
    const mappedPlayers = mapPresenceStateToAppPlayers(presenceState);
    console.log('[useRoomPresence] Mapped players from presence state:', mappedPlayers);
    setPlayers(mappedPlayers);
  };

  // --- Fallback Polling ---
  usePresencePolling({
    roomId,
    isPolling: channelStatus !== 'SUBSCRIBED',
    onPlayersUpdate: (players: any[]) => {
      console.log('[useRoomPresence] Fallback polling players:', players);
      syncPlayersFromDb();
    },
  });

  // --- Presence Update ---
  const { updatePresence } = usePresenceUpdate({ presenceChannel, userId });

  // --- Effect: Initial Sync and Ready State ---
  useEffect(() => {
    if (channelStatus === 'SUBSCRIBED') {
      console.log('[useRoomPresence] Presence channel subscribed. Players:', players);
      setPresenceReady(true);
    } else {
      setPresenceReady(false);
    }
  }, [channelStatus, players]);

  // Add log for presence sync event
  useEffect(() => {
    if (players && players.length >= 0) {
      console.log('[useRoomPresence] Presence sync received. Players:', players);
      setPresenceReady(true);
    }
  }, [players as any[]]);

  // Attach the presence sync handler to the channel
  useEffect(() => {
    if (channel && typeof channel.on === 'function') {
      const handler = () => {
        // Fetch the actual presence state from the channel
        const presenceState = channel.presenceState();
        console.log('[useRoomPresence] Raw presence state received:', presenceState);
        const mappedPlayers = mapPresenceStateToAppPlayers(presenceState);
        console.log('[useRoomPresence] Mapped players from presence state:', mappedPlayers);
        setPlayers(mappedPlayers);
      };
      channel.on('presence', { event: 'sync' }, handler);
      // No-op cleanup
    }
  }, [channel]);

  // Join presence on the main room channel when available
  useEffect(() => {
    if (channel && userId) {
      channel.track({
        user_id: userId,
        // Add more fields as needed (name, avatar, etc.)
      });
      console.log('[useRoomPresence] Called channel.track() for user:', userId);
    }
  }, [channel, userId]);

  // Update presence with new is_ready value
  const updateReadyPresence = async (isReady: boolean) => {
    if (channel && userId) {
      await channel.track({
        user_id: userId,
        is_ready: isReady,
        name: userProfile.name || 'Player',
        avatar_url: userProfile.avatar_url || null,
      });
      console.log('[useRoomPresence] Updated presence with is_ready:', isReady);
    }
  };

  // --- API: Return state and update function ---
  return {
    players,
    presenceReady,
    channelStatus,
    error,
    updatePresence: updateReadyPresence,
  };
}
